import React, { useContext, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useQuery } from "react-query";
import NavBar from "../../components/NavBar";
import SearchFilter from "../../components/search/SearchFilter";
import SearchSidebarFilter from "../../components/search/SearchSidebarFilter";
import DoctorsResultsComp from "../../components/search/DoctorsResultsComp";
import FacilityResultsComp from "../../components/search/FacilityResultsComp";
import IndustryResultsComp from "../../components/search/IndustryResultsComp";
import SearchHashtags from "../../components/search/SearchHashtags";
import PostsResultsComp from "../../components/search/PostsResultsComp";
import EventsResultComp from "../../components/search/EventsResultComp";
import CoursesResultComp from "../../components/search/CoursesResultComp";
import WebinarResultComp from "../../components/search/WebinarResultComp";
import ClinicalStudiesResults from "../../components/search/ClinicalStudiesResults";
import FeedAdvertisement from "../../components/feeds/sidebar/FeedAdvertisement";
import { getAllSearchAgainstKeywordApi } from "../../services/searchApis";
import { useSplashAds } from "../../hooks/useSplashAds";
import { Language } from "../../App";
import { LuSearch, LuSearchX } from "react-icons/lu";

import "./SearchPage.css";

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const headerRef = useRef(null);

  useEffect(() => {
    const updateStickyOffset = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        // 66px (NavBar) + headerHeight + 12px gap
        document.documentElement.style.setProperty(
          "--search-sticky-top",
          `${66 + headerHeight + 12}px`
        );
      }
    };

    updateStickyOffset();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && headerRef.current) {
      resizeObserver = new ResizeObserver(updateStickyOffset);
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener("resize", updateStickyOffset);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateStickyOffset);
      document.documentElement.style.removeProperty("--search-sticky-top");
    };
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const rawKeyword = queryParams.get("keyword") || "";
  const keyword = decodeURIComponent(rawKeyword).trim();

  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();

  const { data: response, isLoading } = useQuery({
    queryKey: ["search-against-keyword", keyword],
    queryFn: () => getAllSearchAgainstKeywordApi({ keyword }),
    enabled: Boolean(keyword),
    refetchOnWindowFocus: false,
  });

  const doctorsData = response?.data?.doctors || [];
  const facilityData = response?.data?.facility || response?.data?.facilities || [];
  const industryData = response?.data?.industry || response?.data?.industries || [];
  const postsData = response?.data?.posts || [];
  const webinarsData = response?.data?.webinars || [];
  const studiesData = response?.data?.studies || [];
  const coursesData = response?.data?.courses || [];
  const eventsData = response?.data?.events || [];
  const hashtagsData = response?.hash_tags || [];

  const totalResultsCount = useMemo(() => {
    return (
      doctorsData.length +
      facilityData.length +
      industryData.length +
      postsData.length +
      webinarsData.length +
      studiesData.length +
      coursesData.length +
      eventsData.length
    );
  }, [
    doctorsData.length,
    facilityData.length,
    industryData.length,
    postsData.length,
    webinarsData.length,
    studiesData.length,
    coursesData.length,
    eventsData.length,
  ]);

  const isGlobalEmpty =
    !isLoading &&
    keyword &&
    totalResultsCount === 0;

  const popularKeywords = ["Cataract", "Glaucoma", "Retina", "Cornea", "Refractive Surgery", "Ophthalmology"];

  return (
    <div className="search-page-wrapper">
      {/* Top Fixed Navigation Bar */}
      <NavBar />

      {/* Top Category Tabs Bar */}
      <div ref={headerRef} className="search-hero-header">
        <div className="search-hero-inner">
          <div className="search-hero-center-col">
            <SearchFilter keyword={keyword} activeType="all" />
          </div>
        </div>
      </div>

      {/* Main 3-Column Search Layout */}
      <div className="search-layout-container">
        {/* Left Column: Vertical Category Nav & Ad */}
        <div className="search-left-col">
          <SearchSidebarFilter
            counts={{
              doctors: doctorsData?.length || 0,
              facility: facilityData?.length || 0,
              industry: industryData?.length || 0,
              posts: postsData?.length || 0,
              webinars: webinarsData?.length || 0,
              studies: studiesData?.length || 0,
              courses: coursesData?.length || 0,
              events: eventsData?.length || 0,
            }}
            loading={isLoading}
          />
          <FeedAdvertisement
            ads={splashAds?.search_left || []}
            loading={!areSplashAdsReady}
          />
        </div>

        {/* Center Column: Main Discovery Results */}
        <div className="search-main-col">
          {/* Results Header directly above results */}
          <div className="search-results-header-inline">
            <h1 className="search-main-heading">
              <span>{lang?.search_results || "Search Results"}</span>
              {keyword && (
                <span className="search-keyword-badge">
                  <LuSearch size={14} />
                  <span>"{keyword}"</span>
                </span>
              )}
            </h1>

            {!isLoading && keyword && totalResultsCount > 0 && (
              <span className="search-results-summary">
                {totalResultsCount} {totalResultsCount === 1 ? (lang?.result || "result") : (lang?.results || "results")} {lang?.found || "found"}
              </span>
            )}
          </div>

          {isGlobalEmpty ? (
            <div className="search-global-empty-state">
              <div className="search-global-empty-icon">
                <LuSearchX />
              </div>
              <h2 className="search-global-empty-title">
                {lang?.no_results_found || "No results found"}
              </h2>
              <p className="search-global-empty-desc">
                {lang?.no_results_desc ||
                  `We couldn't find any matching results for "${keyword}". Try checking for spelling mistakes or explore our popular medical topics.`}
              </p>

              <div className="search-suggested-keywords-row">
                {popularKeywords.map((kw) => (
                  <button
                    key={kw}
                    type="button"
                    className="search-suggested-chip"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      navigate(`/search?keyword=${encodeURIComponent(kw)}`);
                    }}
                  >
                    {kw}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Doctors Section */}
              <DoctorsResultsComp
                loading={isLoading}
                data={doctorsData}
                keyword={keyword}
              />

              {/* Hospital / Clinic Section */}
              <FacilityResultsComp
                loading={isLoading}
                data={facilityData}
              />

              {/* Organization / Company Section */}
              <IndustryResultsComp
                loading={isLoading}
                data={industryData}
              />

              {/* Posts Section */}
              <PostsResultsComp
                loading={isLoading}
                data={postsData}
                keyword={keyword}
              />

              {/* Webinars Section */}
              <WebinarResultComp
                loading={isLoading}
                data={webinarsData}
                keyword={keyword}
              />

              {/* Clinical Studies Section */}
              <ClinicalStudiesResults
                loading={isLoading}
                data={studiesData}
                keyword={keyword}
              />

              {/* Courses Section */}
              <CoursesResultComp
                loading={isLoading}
                data={coursesData}
                keyword={keyword}
              />

              {/* Events Section */}
              <EventsResultComp
                loading={isLoading}
                data={eventsData}
                keyword={keyword}
              />
            </>
          )}
        </div>

        {/* Right Column: Trending Hashtags & Ad */}
        <div className="search-right-col">
          <SearchHashtags data={hashtagsData} />
          <FeedAdvertisement
            ads={splashAds?.search_right || []}
            loading={!areSplashAdsReady}
          />
        </div>
      </div>
    </div>
  );
};

export default Search;
